import { DocumentOrganizationKeys, GroupSettings } from '@griply/types';
import { isDefaultFilterDocument } from '@griply/utils';
import { useGoalById, useSubGoals, useUpdateGoal } from 'features/goals';
import { useSortedTags } from 'features/tags';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { rootId } from 'shared/components/ui/virtualized-tree-list';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { Paths } from 'shared/routes/paths';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { RelationshipUpdate } from 'shared/types/relationship';
import {
  GroupByMode,
  SortingMode,
  TaskGroupBy,
} from 'shared/types/sorting-mode';
import { UpdateTask } from 'shared/types/task';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import {
  noGroupingId,
  ProvidedTaskFilterList,
} from 'shared/views/task-filter-list';

import { defaultFilter, getFilterDocument } from '../../constants';
import { useAllTasksCustomOrderByGrouping } from '../../hooks/use-goal-custom-order-by-grouping';
import { OutletContext } from '../../outlet-context';
import { ArchivedItemsList } from '../archived-items-list';
import { Interactions } from './interactions';
import * as Styled from './sub-items-section.style';

const supportedGroupByModes: TaskGroupBy[] = [
  GroupByMode.Section,
  GroupByMode.Goal,
  GroupByMode.Priority,
  GroupByMode.Type,
  GroupByMode.Deadline,
  GroupByMode.DeadlineDeadline,
  GroupByMode.Tag,
];
const noLifeAreas: LifeArea[] = [];

export type SubItemsSectionProps = {
  fullSize?: boolean;
  grow?: boolean;
  hasLink?: boolean;
};

export const SubItemsSection: React.FC<SubItemsSectionProps> = ({
  fullSize,
  grow,
  hasLink,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { mainScrollRef } = useOutletContext<OutletContext>();
  const goal = useGoalById(id);
  const tags = useSortedTags();
  const subGoals = useSubGoals(id, true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { submit: updateGoal } = useUpdateGoal();
  const { submit: updateTasks } = useUpdateTasks();
  const updateRelationships = useUpdateRelationshipsReferences();

  const progressUrl = useMemo(
    () => replaceUrlParams(Paths.GoalDetailTasks, { id: id! }),
    [id],
  );

  const subGoalIds = useMemo(() => subGoals.map((g) => g.id), [subGoals]);

  const filterDocument = useMemo(
    () => getFilterDocument(goal, subGoalIds),
    [goal, subGoalIds],
  );

  const hasActiveFilters = useMemo(() => {
    const defaults = defaultFilter(goal, subGoalIds);
    return !isDefaultFilterDocument(
      {
        ...filterDocument,
        [DocumentOrganizationKeys.GroupByMode]:
          defaults[DocumentOrganizationKeys.GroupByMode],
        [DocumentOrganizationKeys.SortingMode]:
          defaults[DocumentOrganizationKeys.SortingMode],
      },
      defaults,
    );
  }, [filterDocument, goal, subGoalIds]);

  const filterGoals = useMemo(() => {
    if (!goal) {
      return [];
    }

    const isGoalGrouping = filterDocument.groupByMode === GroupByMode.Goal;

    const visibleSubGoals = subGoals.filter(
      (subGoal) =>
        !(
          isGoalGrouping &&
          (subGoal.archivedAt ||
            (goal.hideCompletedTasks &&
              !!subGoal.completedAt &&
              (subGoal.taskCount ?? 0) === (subGoal.completedTaskCount ?? 0)))
        ),
    );

    if (goal.goalSorting?.length) {
      visibleSubGoals.sort(
        (a, b) =>
          (goal.goalSorting!.indexOf(a.id) + 1 || Number.MAX_VALUE) -
          (goal.goalSorting!.indexOf(b.id) + 1 || Number.MAX_VALUE),
      );
    }

    return [goal, ...visibleSubGoals];
  }, [filterDocument.groupByMode, goal, subGoals]);

  const groupSettings = useMemo<GroupSettings>(
    () => ({
      [GroupByMode.Section]: {
        sections: goal?.sections,
        onUpdateSection: (sectionId: ID, name: string) =>
          updateGoal({
            id: goal!.id,
            sections: goal!.sections?.map((s) =>
              s.id === sectionId ? { ...s, name } : s,
            ),
          }),
        goalId: goal?.id,
        showEmptyGroups: !hasActiveFilters,
      },
      [GroupByMode.Goal]: {
        goalId: goal?.id,
        showEmptyGroups: !hasActiveFilters,
        hideNoGoalGroup: true,
      },
      [GroupByMode.Priority]: { goalId: goal?.id },
      [GroupByMode.Type]: { goalId: goal?.id },
      [GroupByMode.Deadline]: { goalId: goal?.id },
      [GroupByMode.DeadlineDeadline]: { goalId: goal?.id },
      [GroupByMode.Tag]: { goalId: goal?.id },
      [GroupByMode.None]: { goalId: goal?.id },
    }),
    [goal, hasActiveFilters, updateGoal],
  );

  const onReorder = useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: UpdateTask[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      if (!goal) {
        return;
      }

      // Handle task updates (e.g. reparenting/move)
      updateTasks(updatedTasks);
      const groupBy =
        filterDocument[DocumentOrganizationKeys.GroupByMode] ??
        GroupByMode.Section;

      if (groupBy === GroupByMode.Section) {
        // Create a map of existing sections for quick lookup
        const sectionMap = new Map(
          (goal.sections ?? []).map((section) => [section.id, section]),
        );

        const newSections: typeof goal.sections = [];
        let rootOrder = goal.sections?.map(({ id }) => id) ?? [];

        // Reconstruct the sections array based on the updated order
        updatedSections.forEach((updatedSection) => {
          if (updatedSection.id === rootId) {
            // when root, we abuse the taskSorting to store the sections order
            rootOrder = updatedSection.taskSorting;
            return;
          }

          const originalSection = sectionMap.get(updatedSection.id);

          if (originalSection) {
            newSections.push({
              ...originalSection,
              tasks: updatedSection.taskSorting,
            });
            sectionMap.delete(updatedSection.id);
          }
        });

        // Add any remaining sections that weren't in the update (safety fallback)
        sectionMap.forEach((remainingSection) =>
          newSections.push(remainingSection),
        );

        // Use the correct sorting
        newSections.sort(
          (a, b) => rootOrder.indexOf(a.id) - rootOrder.indexOf(b.id),
        );

        updateGoal({
          id: goal.id,
          sections: newSections,
          taskSorting:
            updatedSections.find((s) => s.id === noGroupingId)?.taskSorting ??
            goal.taskSorting,
          taskSortingMode: SortingMode.Custom,
          filterDocument: {
            ...(goal.filterDocument ?? {}),
            sortingMode: SortingMode.Custom,
          },
        });

        // handle relationship updates if provided
        if (relationshipUpdates?.length) {
          updateRelationships(relationshipUpdates);
        }
        return;
      }

      // handle sorting update for all groupings except 'noGrouping'
      const groupBySorting = updatedSections.reduce<Record<ID, ID[]>>(
        (acc, section) => {
          if (section.id === rootId) {
            return acc;
          }
          acc[section.id] = section.taskSorting;
          return acc;
        },
        {},
      );

      // Handle goal reordering
      if (groupBy === GroupByMode.Goal) {
        updatedSections.forEach((section) => {
          // only happens when reordering sub-goals
          if (section.id === rootId) {
            updateGoal({
              id: goal.id,
              goalSorting: section.taskSorting,
            });
            return;
          }

          // only happens when reordering tasks
          if (section.id === goal.id) {
            updateGoal({
              id: goal.id,
              taskSorting: section.taskSorting,
              taskSortingMode: SortingMode.Custom,
              filterDocument: {
                ...(goal.filterDocument ?? {}),
                sortingMode: SortingMode.Custom,
              },
            });
            return;
          }

          const subGoal = subGoals.find((g) => g.id === section.id);

          if (subGoal) {
            updateGoal({
              id: section.id,
              taskSorting: section.taskSorting,
              taskSortingMode: SortingMode.Custom,
              filterDocument: {
                ...(subGoal.filterDocument ?? {}),
                sortingMode: SortingMode.Custom,
              },
            });
          }
        });
        return;
      }

      if (Object.keys(groupBySorting).length) {
        updateGoal({
          id: goal.id,
          customTaskOrder: {
            ...(goal.customTaskOrder ?? {}),
            [groupBy as Exclude<
              GroupByMode,
              GroupByMode.None | GroupByMode.LifeArea
            >]: {
              ...(goal.customTaskOrder?.[
                groupBy as Exclude<
                  GroupByMode,
                  GroupByMode.None | GroupByMode.LifeArea
                >
              ] ?? {}),
              ...groupBySorting,
            },
          },
          taskSortingMode: SortingMode.Custom,
          filterDocument: {
            ...(goal.filterDocument ?? {}),
            sortingMode: SortingMode.Custom,
          },
        });
      }

      // handle relationship updates if provided
      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [
      filterDocument,
      goal,
      updateGoal,
      updateRelationships,
      updateTasks,
      subGoals,
    ],
  );

  const onSectionCollapse = useCallback(
    (collapsedSections: ID[]) => {
      if (!goal) {
        return;
      }

      updateGoal({
        id: goal.id,
        filterDocument: {
          ...(goal.filterDocument ?? {}),
          collapsedSections,
        },
      });
    },
    [goal, updateGoal],
  );

  const onUpdateFilterDocument = useCallback(
    (newFilterDocument: typeof filterDocument) => {
      if (!goal) {
        return;
      }
      updateGoal({
        id: goal.id,
        filterDocument: newFilterDocument,
      });
    },
    [goal, updateGoal],
  );

  const onResetFilter = useCallback(() => {
    if (!goal) {
      return;
    }
    updateGoal({
      id: goal.id,
      filterDocument: defaultFilter(goal, subGoalIds),
    });
  }, [goal, subGoalIds, updateGoal]);

  const customOrder = useAllTasksCustomOrderByGrouping(
    filterDocument.groupByMode,
    goal,
    subGoals,
  );

  if (!goal) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.goal-detail.tasks.title')}
      url={hasLink ? progressUrl : undefined}
      fullSize={fullSize}
      grow={grow}
      interactions={
        <Interactions
          filterDocument={filterDocument}
          defaultFilterDocument={defaultFilter(goal, subGoalIds)}
          updateFilterDocument={onUpdateFilterDocument}
          onReset={onResetFilter}
          goals={filterGoals}
          tags={tags}
          lifeAreas={noLifeAreas}
          supportedGroupByModes={supportedGroupByModes}
          hideNoGoal
        />
      }
      noPadding
    >
      <Styled.ScrollContainer ref={scrollContainerRef} $scrollAuto={!!fullSize}>
        <ProvidedTaskFilterList
          filterDocument={filterDocument}
          defaultFilterDocument={defaultFilter(goal, subGoalIds)}
          customOrder={customOrder}
          onReorder={onReorder}
          onSectionCollapse={onSectionCollapse}
          scrollElement={
            fullSize ? scrollContainerRef : mainScrollRef ?? scrollContainerRef
          }
          groupSettings={groupSettings}
          onReset={onResetFilter}
          goals={filterGoals}
          canReorderGoals
        />

        <ArchivedItemsList goal={goal} />
      </Styled.ScrollContainer>
    </PageSectionBase>
  );
};
